import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Building2, Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { clearNeedsWorkspacePick, markNeedsWorkspacePick, needsWorkspacePick } from "@/lib/workspacePick";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { APP_HOME_PATH } from "@/lib/copilot-home";

export default function SelectWorkspacePage() {
  const { session, loading, workspaces, switchWorkspace, signOut } = useAuth();
  const brand = useBrand();
  const [picking, setPicking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [open, setOpen] = useState(false);
  const sorted = useMemo(
    () => [...workspaces].sort((a, b) => (a.organization_name ?? "").localeCompare(b.organization_name ?? "")),
    [workspaces],
  );

  useEffect(() => {
    if (!loading && session && workspaces.length <= 1) {
      clearNeedsWorkspacePick();
    }
  }, [loading, session, workspaces.length]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;

  if (workspaces.length <= 1) {
    return <Navigate to={APP_HOME_PATH} replace />;
  }

  if (!needsWorkspacePick()) {
    // Cambio manual desde sesión: también permitir elegir aquí, pero si no hay flag
    // y ya hay sesión activa, redirigir al app (el switcher del sidebar cubre el día a día).
    return <Navigate to={APP_HOME_PATH} replace />;
  }

  const handlePick = async (organizationId: string) => {
    setPicking(organizationId);
    setError(null);
    try {
      clearNeedsWorkspacePick();
      await switchWorkspace(organizationId, APP_HOME_PATH);
    } catch (e: unknown) {
      setError((e as { message?: string })?.message || "No se pudo cambiar de workspace");
      markNeedsWorkspacePick();
      setPicking(null);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden px-4"
      style={{ backgroundColor: brand.palette.deepBg }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full blur-[120px]"
          style={{ backgroundColor: `${brand.palette.primary}33` }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl">
        <div className="text-center mb-6">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <h1 className="font-display text-2xl font-bold text-white mb-1">Elige un workspace</h1>
          <p className="text-sm text-white/60">
            Tu cuenta pertenece a varios workspaces. Selecciona con cuál quieres continuar.
          </p>
        </div>

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={!!picking}
              className="w-full justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white hover:bg-white/10 hover:text-white focus:ring-1 focus:ring-white/20"
            >
              {selected
                ? sorted.find((ws) => ws.organization_id === selected)?.organization_name
                : "Selecciona un workspace..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-white/40" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl border border-white/10 bg-[#1a1a2e] backdrop-blur-xl">
            <Command className="bg-transparent">
              <CommandInput placeholder="Buscar workspace..." className="text-white placeholder:text-white/40" />
              <CommandList>
                <CommandEmpty className="py-4 text-center text-sm text-white/40">
                  No se encontraron workspaces
                </CommandEmpty>
                <CommandGroup>
                  {sorted.map((ws) => (
                    <CommandItem
                      key={ws.organization_id}
                      value={ws.organization_name ?? ""}
                      onSelect={() => {
                        setSelected(ws.organization_id);
                        setOpen(false);
                      }}
                      className="rounded-lg text-sm text-white/90 aria-selected:bg-white/10 aria-selected:text-white"
                    >
                      <Check className={cn("mr-2 h-4 w-4", selected === ws.organization_id ? "opacity-100" : "opacity-0")} />
                      <Building2 className="mr-2 h-4 w-4 shrink-0 text-white/70" />
                      {ws.organization_name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Button
          disabled={!selected || !!picking}
          onClick={() => void handlePick(selected)}
          className="mt-4 w-full rounded-xl py-3 text-sm font-medium"
          style={{ backgroundColor: brand.palette.primary, color: "#fff" }}
        >
          {picking ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Continuar"}
        </Button>

        {error && <p className="mt-3 text-sm text-red-300">{error}</p>}

        <div className="mt-6 flex justify-center">
          <Button
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/10"
            onClick={() => {
              clearNeedsWorkspacePick();
              void signOut();
            }}
          >
            Cerrar sesión
          </Button>
        </div>
      </div>
    </div>
  );
}
